import React, { useEffect, useMemo } from 'react';
import useHotkey from '../hooks/use-hotkey';
import { setHotkey } from '@form-engine/builder-application/keybindings';
import { useFormContext } from '@form-engine/core-application/hooks/use-form';
import { useCallback } from 'react';
import {
    BuilderConfirmModal,
    BuilderModalProps,
} from '@form-engine/builder-application/components/modal';
import dialog, {
    confirm,
} from '@form-engine/builder-application/components/dialog';
import {
    FormDefinition,
    FormPreset,
} from '@form-engine/core-application/types/form';
import { i18n } from '@form-engine/builder-application';
import { useFormEditorContext } from '../hooks/use-form-editor';
import { StoreMaskProvider } from '@form-engine/builder-mask/hooks/use-mask';
import { useStore } from '@form-engine/core-application/hooks/use-store';
import { Nullable, Optional } from '@form-engine/core-util/typings';
import { createPresetDataFromElement } from '../utilities/form-preset';
import useFormPresetApi, {
    refreshFormPresets,
} from '@form-engine/builder-application/hooks/use-form-preset-api';
import { ApiListResult } from '@form-engine/builder-application/hooks/use-api-model';
import BuilderFormPresetMask from '../components/form-preset-dialog/mask';
import z from 'zod';
import strictSchema from '../components/form-preset-dialog/schema';

const schema = strictSchema
    .omit({ id: true })
    .and(z.object({ id: z.string().default('') }));

function useUpsertFormPreset() {
    const model = useFormPresetApi();

    return useCallback(
        async (preset: FormPreset) => {
            let prev: FormPreset | undefined;

            // if preset is not new, check if the server side still exists (otherwise treat this as new)
            if (preset?.id) {
                prev = await model.retrieve(preset.id);
                // if preset is new, check if it should override one with the same name
            } else if (preset?.name) {
                [prev] = (await model.list({
                    filter: { name: preset.name },
                    attributes: ['id'],
                    limit: 1,
                    verbose: false,
                })) as ApiListResult<FormPreset>;

                if (
                    prev &&
                    !(await confirm({
                        children: i18n.c(
                            'form-editor.savePresetDialog.confirmOverrideMessage',
                            { name: preset.name }
                        ),
                    }))
                ) {
                    return prev;
                }
            }

            let result: FormPreset;

            if (prev?.id) {
                result = await model.update(
                    structuredClone({
                        ...preset,
                        id: prev?.id,
                    })
                );
            } else {
                const dto: Partial<FormPreset> = structuredClone(preset);
                delete dto.id;
                result = await model.create(dto);
            }

            return result;
        },
        [model]
    );
}

const defaultPreset = schema.parse({}) as FormPreset;

export function SavePresetDialog({
    title = <i18n.Node id="form-editor.savePresetDialog.title" />,
    form,
    selectedId,
    onSuccess,
    onError,
    ...props
}: BuilderModalProps & {
    form: FormDefinition;
    selectedId?: string;
    onSuccess?: (result: FormPreset) => void;
    onError?: (error: Error) => void;
}) {
    const data: Nullable<FormDefinition> = useMemo(() => {
        if (!form?.elements || !selectedId) return null;
        return createPresetDataFromElement(form, selectedId);
    }, [form, selectedId]);

    const presetStore = useStore<FormPreset>(defaultPreset);

    useEffect(() => {
        if (data?.elements?.length && presetStore?.setItem) {
            presetStore?.setItem('data', structuredClone(data));
        }
    }, [presetStore?.setItem]);

    const schemaError = useMemo(() => {
        const parseResult = schema.safeParse(presetStore?.state);
        if (parseResult.success) return null;
        return parseResult.error;
    }, [presetStore?.state]);

    const upsert = useUpsertFormPreset();

    const handleSubmit = useCallback(async () => {
        if (!presetStore?.state || schemaError) return;

        try {
            const result = await upsert(presetStore?.state);
            onSuccess?.(result);
        } catch (e) {
            onError?.(e as Error);
        }
    }, [presetStore?.state, onSuccess, onError, upsert, schemaError]);

    if (!presetStore) return null;

    return (
        <BuilderConfirmModal
            size="lg"
            {...props}
            disabled={Boolean(schemaError)}
            onConfirm={handleSubmit}
            title={title}
        >
            <StoreMaskProvider store={presetStore}>
                <BuilderFormPresetMask />
            </StoreMaskProvider>
        </BuilderConfirmModal>
    );
}

export default function useSavePresetAction() {
    const { state: form } = useFormContext();
    const { state: editor } = useFormEditorContext();
    return useCallback(async () => {
        try {
            // const preset =
            (await dialog({
                as: SavePresetDialog,
                form,
                selectedId: editor?.selectedId,
            })) as Optional<FormPreset>;
            refreshFormPresets();
        } catch (e) {
            console.error(e);
            return;
        }
    }, [form, editor?.selectedId]);
}

export const ACTION_NAME = 'formEditor.selection.showPresetActions';

setHotkey(ACTION_NAME, 'ctrl+p,meta+p');

export const useAction = useSavePresetAction;

export function useActionHotkey() {
    useHotkey(ACTION_NAME, useAction());
}
